import { DocumentReference } from "@angular/fire/firestore";
import { PaymentMethod } from "@stripe/stripe-js";

export const DefaultMessages = {
    noLoginWelcomeMessgae: "<b>Hey there! 👋 Welcome to AnyStore. I'm your smart shopping assistant — just text or speak your grocery list, and I'll shop it for you, store to door.</b><br><br><b>No store restrictions. No markups. No stress.</b> — Groceries at store prices, delivered your way.<br> Sign in to get started — it's quick and easy!",
    loginOptionSelected: "Let me login",
    phoneLoginPrompt: `<b>We'll use your phone number to verify your account — simple and secure.</b><br>🔒 We'll never share it — your privacy is our priority.<br><br>By continuing, you agree to our <a href="https://www.anystore.app/privacy" class="text-blue-600">Privacy</a> and <a href="https://www.anystore.app/terms" class="text-blue-600">Terms</a>`,
    verifyOTPPrompt: "Please enter One Time Passcode sent to your phone number",
    LoginSuccessPrompt: "Hurray! You are now logged it. Try one of the default functions or type/say to add items to cart"
}

export enum MessageType {
    info = 'info',
    message = 'message',
    action = 'action',
    json = 'json',
    loader = 'loader'
}

export interface IMessage {
    message: string;
    timestamp: number;
    reply: boolean;
    type?: string;
    actions?: { label: string; value: string; action: () => void; }[];
    reference?: string;
    cartItem?: CartItem;
    orderDetails?: OrderDetails;
    suggestions?: {
        basedOnPastOrders: CartItem[],
        relatedItems: CartItem[],
        actualMessage: string;
    };
    sentBy?: string;
}

export interface CartItem {
    id?: string;
    name: string;
    quantity?: string;
    unit?: string;
    store?: string;
    location?: string;
    userPrompt?: string;
    images?: Array<GCPImage>;
    notes?: string;
    shopperNotes?: string;

    status?: 'found' | 'replaced' | 'not_found';
    orderedOn?: number;
}

export enum ImageCategoryPath {
    CartItem = 'grocery_item_images',
    ProofOfDelivery = 'proof_of_delivery',
    ShoppingReceipts = 'shopping_receipts',
    ShopperDriversLicense = 'shopper_documents/drivers_license',
    ShopperSelfie = 'shopper_documents/selfie',
    ShopperVehicleInsurance = 'shopper_documents/vehicle_insurance'
}

export interface GCPImage {
    downloadUrl: string;
    bucket: string;
    contentType: string;
    fullPath: string;
    name: string;
    size: number;
    timeCreated: string;
    updated: string;
}

export interface StoreReceipt {
    storeName: string,
    total: number;
    receiptImage: GCPImage | null;
    createdAt?: number;
    createdAtString?: string;
}

export interface DeliveryLocation {
    uid?: string;
    lat: number;
    lng: number;
    fullAddress: string;
    streetAddress: string;
    city: Option;
    zipcode: string;
    state: Option;
    country: Option;
    preferred?: boolean;
    createdOn?: number;
}
export interface OrderDetails {
    orderId?: string;
    orderNumber: string;
    items: Array<CartItem>;
    orderStatus: 'order placed' | 'order accepted' | 'shopping inprogress' | 'shopping completed' | 'on the way' | 'delivered' | 'cancelled' | string;
    orderedDateTime: number;
    orderedDateTimeString: string;
    orderUpdatedDateTime: number;
    orderUpdatedDateTimeString: string;
    orderUpdatedById?: string;
    total?: number;
    storeReceipts?: StoreReceipt[];
    shoppingFee: number;
    multiStoreShoppingFee: number;
    totalShoppingFee: number;
    totalCartItems: number;
    serviceFee: number;
    salesTax: number;
    deliveryFee: number;
    totalDeliveryFee: number;
    multiStoreDeliveryFee: number;
    farApartStoresFee: number;
    totalDistance: number;
    totalTravelTime: number;
    stores: {
      name: string,
      allItemsMarked?: boolean,
      hasReceipt?: boolean,
      shoppingCompleted?: boolean
    }[];
    storeAddresses: {name: string, address: string, lat: number, lng: number}[];
    storeCount: number;
    storeLocations: string[];
    totalFee: number;
    tip: number;
    paymentMethodId?: string;
    paymentMethodDetails?: PaymentMethod,
    PaymentStatus: 'success' | 'pending' | 'failed' | 'rejected' | 'refunded';
    estimatedDeliveryWindow?: string;
    estimatedDeliveryDateTime?: number;
    estimatedDeliveryDateTimeString?: string;
    deliveryDateTime?: number;
    deliveryDateTimeString?: string;
    deliveryLocation: DeliveryLocation;
    batchId?: string;
    estimatedShopperPay?: number;
    distanceCovered?: number;
    shoppingTime?: number;
    deliveryTime?: number;
    gasSaved?: number;
    timeSaved?: number;
    energySaved?: number;
    paymentIntent?: OrderPaymentIntent;
    proofOfDeliveryImages: GCPImage[];

    customerPreferences: UserPersonalization;
    stripeCustomerId: string;
    createdById: string;
    createdByName: string;
    orderDocumentRef?: DocumentReference;

    acceptedById?: string;
    acceptedByName?: string;
    acceptedByPhone?: string;
    acceptedAt?: number;
    acceptedAtString?: string;
    completedById?: string;

    pricingMetricsUsed?: PricingMetrics;
    transactionId?: string;
    routeMetrics?: {
        totalDistanceMiles: number;
        extraFee: number;
        storeLocations: {name: string, address: string, lat: number, lng: number}[];
        deliveryLocation: {lat: number, lng: number};
        routeLegs: {startAddress: string, endAddress: string, distanceMiles: number, durationMins: number}[];
    };
}

export enum OrderStatus {
    OrderPlaced = 'order placed',
    OrderAccepted = 'order accepted', 
    ShoppingInProgress = 'shopping inprogress', 
    ShoppingCompleted = 'shopping completed', 
    OnTheWay = 'on the way', 
    Delivered = 'delivered',
    Cancelled = 'cancelled'
}
export const OrderStatusProgressOrder = [OrderStatus.OrderPlaced, OrderStatus.OrderAccepted, OrderStatus.ShoppingInProgress, OrderStatus.ShoppingCompleted, OrderStatus.OnTheWay, OrderStatus.Delivered];
export const ActionButtonLabelsForOrderStatus = ['Accept Order', 'Start Shopping', 'Mark as Shopping Complete', 'Navigate To Dropoff Address', 'Mark as Delievred', 'Completed'];


export interface OrderPaymentIntent {
    publishableKey: string;
    paymentIntentClientSecret: string;
    customerId: string;
    paymentIntentId: string;
}

export interface CreateOrderChargePayload {
    stripeCustomerId: string;
    amount: number;
    currency: string;
    paymentMethodId: string;
    returnUrl: string;
}

export interface UserPersonalization {
    skipped: boolean;
    favoriteStores: string,
    diet: string,
    organicVarient: string;
    regionalPreferences: string;
    updatedAt: number;
}
export interface UserProfile {
    name: string;
    uid: string;
    phoneNumber: string;
    email: string;
    photoUrl: string;
    roles: string[];
    lastLoginAt: string;
    createdAt: string;
    currentLocation?: {latitude: number, longitude: number, timestamp: number};

    personalization?: UserPersonalization;

    permissions: unknown;

    previousAddresses: Array<DeliveryLocation>;

    devicePushToken?: string;
    stripeCustomerId: string;

    // shopper related fields
    isAvailable: boolean;
    onboardingCompleted: boolean;
    onboarding?: {
        driversLicense?: GCPImage[];
        vehicleInsurance?: GCPImage[];
        vehicle?: GCPImage[];
        selfie?: GCPImage[];
        fullName?: string;
        email?: string;
        dateOfBirth?: string;
        address?: string;
        driversLicenseNumber?: string;
        driversLicenseExpirationDate?: string;
        driversLicenseIssueDate?: string;
        driversLicenseIssueState?: string;
        hasDriversLicense?: 'yes' | 'no';
        hasVehicle?: 'yes' | 'no';
        hasOtherAppsExperience?: 'yes' | 'no';
        hasVehicleInsurance?: 'yes' | 'no';
        groceryShoppingExperience?: 'Occasional' | 'Regular' | 'Expert';
        vehicleType?: string;
        completedOn?: number;
        completedOnString?: string;
        updatedOn?: number;
        updatedOnString?: string;
        createdOn?: number;
        createdOnString?: string;
    }
    rating?: number;
    stripeCardId?: string;
    stripeCardholderId?: string;
}

export interface Option {
    name: string;
    code: string;
}

export interface Address {
    streetAddress: string;
    city: Option;
    state: Option;
    zipcode: string;
    country: Option;
    lat?: number;
    lng?: number;
}

export interface PricingMetrics {
    shoppingFee: {
        maximum: number;
        minimum: number;
        perAdditionalStore: number;
        perItem: number;
        updatedDate: string;
    },
    deliveryFee: {
        thresholdKm: number;
        perKm: number;
        maximum: number;
        minimum: number;
        perMile: number;
        perAdditionalStore: number;
        updatedDate: string;
    },
    salesTax: {
        california: number;
    },
    serviceFee: {
        maximum: number;
        minimum: number;
        percent: number;
        updatedDate: string;
    },
};

export const GroceryCategorySortRanking = {
    'Fruits': 1,
    'Vegetables': 2,
    'Dairy': 3,
    'Meat & Seafood': 4,
    'Canned Goods': 5,
    'Frozen Foods': 6,
    'Bakery': 7,
    'Beverages': 8,
    'Pantry Staples': 9,
    'Spices & Masalas': 10,
    'Snacks': 11
}


export const ranchoCordovaGeoJSON = {"type": "Feature", "geometry": {"type": "Polygon", "coordinates": [[[-121.32736, 38.57701], [-121.32754, 38.57737], [-121.32737, 38.57775], [-121.32735, 38.57884], [-121.32781, 38.57895], [-121.32781, 38.57991], [-121.32808, 38.58009], [-121.3279, 38.58912], [-121.33164, 38.59447], [-121.33149, 38.59687], [-121.33105, 38.59754], [-121.32614, 38.602], [-121.32479, 38.60317], [-121.32421, 38.60347], [-121.31786, 38.60251], [-121.31516, 38.60451], [-121.30321, 38.61416], [-121.30363, 38.62477], [-121.29149, 38.63027], [-121.28873, 38.6285], [-121.28961, 38.62587], [-121.28606, 38.6206], [-121.27141, 38.63178], [-121.26899, 38.63339], [-121.26653, 38.62901], [-121.26602, 38.62775], [-121.26584, 38.62666], [-121.26596, 38.62547], [-121.26815, 38.61837], [-121.26639, 38.61859], [-121.26456, 38.61135], [-121.22841, 38.625], [-121.21713, 38.63094], [-121.21172, 38.62347], [-121.2113, 38.62362], [-121.20943, 38.62082], [-121.21157, 38.62001], [-121.21208, 38.62078], [-121.21268, 38.62054], [-121.21418, 38.62254], [-121.21895, 38.62165], [-121.21737, 38.62046], [-121.22157, 38.61921], [-121.22162, 38.61975], [-121.22352, 38.61934], [-121.22359, 38.61946], [-121.23018, 38.61909], [-121.23178, 38.62138], [-121.23234, 38.6217], [-121.23265, 38.62167], [-121.23195, 38.62093], [-121.22984, 38.61717], [-121.22989, 38.61113], [-121.2299, 38.61024], [-121.23275, 38.61021], [-121.2328, 38.60279], [-121.2355, 38.60277], [-121.23552, 38.60073], [-121.2329, 38.60076], [-121.2329, 38.59846], [-121.23517, 38.59793], [-121.23825, 38.59786], [-121.243, 38.59674], [-121.24294, 38.59192], [-121.23765, 38.59228], [-121.2214, 38.59255], [-121.20062, 38.59346], [-121.20061, 38.5839], [-121.1981, 38.58389], [-121.19826, 38.57853], [-121.195, 38.57851], [-121.1925, 38.58453], [-121.18494, 38.58457], [-121.18739, 38.58113], [-121.18722, 38.55914], [-121.18726, 38.5367], [-121.1874, 38.53558], [-121.18788, 38.53425], [-121.18858, 38.53313], [-121.18953, 38.53212], [-121.22327, 38.50191], [-121.2339, 38.50425], [-121.24246, 38.50576], [-121.2427, 38.5246], [-121.24288, 38.52732], [-121.24298, 38.55256], [-121.24381, 38.55254], [-121.24411, 38.5534], [-121.24487, 38.5542], [-121.24953, 38.55758], [-121.25086, 38.55808], [-121.25313, 38.55849], [-121.25351, 38.55868], [-121.25382, 38.5591], [-121.25565, 38.55909], [-121.25571, 38.56278], [-121.25981, 38.56843], [-121.26845, 38.56797], [-121.27441, 38.56799], [-121.27508, 38.56912], [-121.2773, 38.56779], [-121.29018, 38.56701], [-121.29084, 38.56727], [-121.29075, 38.56625], [-121.30033, 38.56588], [-121.30037, 38.56639], [-121.30847, 38.566], [-121.3146, 38.56283], [-121.31776, 38.56074], [-121.32012, 38.55972], [-121.32765, 38.55743], [-121.3362, 38.55675], [-121.33647, 38.57247], [-121.32736, 38.57701]]]}, "properties": {}}


export const folsomGeoJSON = {"type": "Feature", "geometry": {"type": "Polygon", "coordinates": [[[-121.20786, 38.63583], [-121.20857, 38.63676], [-121.20849, 38.63706], [-121.20815, 38.63715], [-121.20755, 38.6399], [-121.20616, 38.6403], [-121.19456, 38.64576], [-121.19256, 38.6501], [-121.19264, 38.65056], [-121.18965, 38.66433], [-121.18888, 38.66666], [-121.18889, 38.67127], [-121.18987, 38.67346], [-121.18979, 38.67526], [-121.18749, 38.67711], [-121.18801, 38.67736], [-121.19127, 38.67986], [-121.19366, 38.67845], [-121.19772, 38.67673], [-121.19865, 38.676], [-121.20018, 38.67429], [-121.20105, 38.6755], [-121.20103, 38.67592], [-121.20078, 38.67628], [-121.20077, 38.67713], [-121.20042, 38.67714], [-121.20047, 38.6781], [-121.19996, 38.68172], [-121.19757, 38.68167], [-121.19724, 38.6845], [-121.19606, 38.68451], [-121.1959, 38.68485], [-121.19594, 38.68522], [-121.19558, 38.68575], [-121.19277, 38.68665], [-121.19256, 38.69252], [-121.19263, 38.69967], [-121.19438, 38.69969], [-121.19369, 38.71391], [-121.19355, 38.714], [-121.18873, 38.71388], [-121.18857, 38.71398], [-121.18857, 38.71431], [-121.18178, 38.71375], [-121.17874, 38.71386], [-121.17629, 38.71336], [-121.17376, 38.7131], [-121.17007, 38.71299], [-121.15186, 38.7115], [-121.14182, 38.71212], [-121.14021, 38.71171], [-121.13872, 38.71058], [-121.13824, 38.70873], [-121.13774, 38.70819], [-121.13572, 38.7079], [-121.13498, 38.7076], [-121.13457, 38.70721], [-121.13442, 38.70666], [-121.13394, 38.70631], [-121.13326, 38.70543], [-121.13215, 38.7056], [-121.13192, 38.70584], [-121.13026, 38.7065], [-121.12905, 38.70828], [-121.12807, 38.70885], [-121.12707, 38.70895], [-121.12661, 38.70919], [-121.12573, 38.71006], [-121.12479, 38.71031], [-121.12396, 38.71027], [-121.12324, 38.71082], [-121.12099, 38.7143], [-121.11985, 38.71441], [-121.1187, 38.71519], [-121.11876, 38.71695], [-121.11862, 38.71712], [-121.11142, 38.70243], [-121.11068, 38.70041], [-121.10878, 38.69693], [-121.10435, 38.68705], [-121.08556, 38.6481], [-121.09643, 38.64449], [-121.0991, 38.64408], [-121.14748, 38.64194], [-121.15191, 38.64195], [-121.17215, 38.64284], [-121.17509, 38.6427], [-121.19675, 38.64004], [-121.20003, 38.6361], [-121.20138, 38.63494], [-121.20335, 38.6339], [-121.20873, 38.63187], [-121.20971, 38.63335], [-121.21014, 38.6346], [-121.20786, 38.63583]]]}, "properties": {}}

export interface Batch {
    batchId: string;
    orders: OrderDetails[];
    stores: { name: string; lat: number; lng: number, address: string, orderId: string }[];
    deliveryLocations: DeliveryLocation[];
    status: 'available' | 'accepted' | 'completed' | 'cancelled' | 'in_progress';
    shopperId?: string;
    acceptedAt?: number;
    acceptedAtString?: string;
    completedAt?: number;
    completedAtString?: string;
    createdAt?: number;
    createdAtString?: string;
   
    totalItems: number;
    totalTip: number;
    estimatedHourlyPay: number;
    estimatedShopperPay: number;
    estimatedShoppingTime: number;
    estimatedDistance?: number;
    optimizedRoute?: OptimizedRoute[];
    estimatedTravelDistance: number;
    estimatedTravelDistanceInMiles: number;
    estimatedTravelTime: number;
    estimatedTravelTimeInMinutes: number;
    estimatedTotalTimeInMinutes: number;
    estimatedTotalTimeInHours: number;
    legs: [];

    // post completion metrics
    actualShopperPay: number;
    actualShoppingTime: number;
    actualTravelDistance: number;
    actualTravelTime: number;
    actualTotalTime: number;
    actualTotalTimeInHours: number;
}

export interface OptimizedRoute {
    name: string;
    orderIds: string[];
    orderRefs: DocumentReference[];
    orderCount: number;
    itemsCount: number;
    storeCount: number;
    shoppingStatus: 'not_started' | 'in_progress' | 'completed';
    deliveryStatus: 'not_started' | 'on_the_way' | 'completed';
    fullAddress: string;
    lat: number;
    lng: number;
    streetAddress: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
}